import { assignTokens } from "@sail/engine";
import { tokens } from "@sail/ui";
import { calculateEmphasizedFontWeight, calculateFontSizeRatio, getScaledFontSize, parseFontSizeUnit, scaleTokenNumber } from "./tokenUtils";
import { themeValueDefaults } from "./appearanceOptionsDefaults";
import { getAccessibleColorFromBackgroundValue, getAccessibleTextColor, getAccessibleBorderColor, getAccessibleColor, deriveColorFromValue, shouldUseDarkOrLightContrast, blendColors, invertColor } from "./contrastUtils";
import { getBrand700Color, getFormTextHoverColor, getColor } from "./utils";
import { assertUnreachable } from "./assertUnreachable";
const MOBILE_SDK_THEME_DEFAULTS = {
    buttonLabelFontSize: '16px',
    buttonLabelFontWeight: '400',
    buttonPaddingXScale: 2,
    buttonPaddingYScale: 1,
    inputFieldPaddingXScale: 1.5,
    inputFieldPaddingYScale: 1
};
export const getFontFamily = (theme)=>theme.fontFamily;
// Background Colors
export const getColorBackground = (theme)=>theme.colorBackground;
export const getColorBackgroundForCalculationsFromValue = (val)=>val || themeValueDefaults.colorBackground;
export const getColorBackgroundForCalculations = (theme)=>getColorBackgroundForCalculationsFromValue(getColorBackground(theme));
export const getAccessibleColorFromBackground = (theme, darkContrast, lightContrast)=>{
    const backgroundColor = getColorBackgroundForCalculations(theme);
    return getAccessibleColorFromBackgroundValue(backgroundColor, darkContrast, lightContrast);
};
// Primary Button
export const getPrimaryColor = (theme)=>{
    var _theme_colorPrimary;
    return (_theme_colorPrimary = theme.colorPrimary) !== null && _theme_colorPrimary !== void 0 ? _theme_colorPrimary : themeValueDefaults.colorPrimary;
};
export const getPrimaryColorHovered = (theme)=>deriveColorFromValue(getPrimaryColor(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
export const getButtonPrimaryColorBackground = (theme)=>theme.buttonPrimaryColorBackground || getPrimaryColor(theme);
export const getButtonPrimaryColorBorder = (theme)=>theme.buttonPrimaryColorBorder || getAccessibleBorderColor(theme.buttonPrimaryColorBackground || getPrimaryColor(theme), theme);
export const getButtonPrimaryColorText = (theme)=>theme.buttonPrimaryColorText || getAccessibleTextColor(getButtonPrimaryColorBackground(theme));
export const getColorPrimaryButtonIcon = (theme)=>getAccessibleColor(getButtonPrimaryColorText(theme), [
        150,
        0
    ]);
export const getButtonPrimaryColorBackgroundPressed = (theme)=>deriveColorFromValue(getButtonPrimaryColorBackground(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
export const getButtonPrimaryColorBorderHovered = (theme)=>{
    const borderColor = getButtonPrimaryColorBorder(theme);
    return borderColor && getBrand700Color(borderColor);
};
export const getButtonPrimaryColorBorderPressed = (theme)=>getButtonPrimaryColorBorderHovered(theme);
// Secondary Button
export const getButtonSecondaryColorBackground = (theme)=>theme.buttonSecondaryColorBackground || themeValueDefaults.buttonSecondaryColorBackground;
export const getButtonSecondaryColorBorder = (theme)=>theme.buttonSecondaryColorBorder || getAccessibleBorderColor(theme.buttonSecondaryColorBackground || getButtonSecondaryColorBackground(theme), theme);
export const getButtonSecondaryColorText = (theme)=>theme.buttonSecondaryColorText || getAccessibleTextColor(getButtonSecondaryColorBackground(theme));
export const getColorSecondaryButtonIcon = (theme)=>getAccessibleColor(getButtonSecondaryColorText(theme), [
        150,
        0
    ]);
export const getButtonSecondaryColorBackgroundPressed = (theme)=>deriveColorFromValue(getButtonSecondaryColorBackground(theme), [
        900,
        850
    ], [
        0,
        50
    ]);
export const getButtonSecondaryColorBorderHovered = (theme)=>getAccessibleColor(getButtonSecondaryColorBorder(theme), [
        0,
        150
    ]);
// Danger Button
export const getButtonDangerColorBackground = (theme)=>theme.buttonDangerColorBackground || themeValueDefaults.buttonDangerColorBackground;
export const getButtonDangerColorBackgroundPressed = (theme)=>deriveColorFromValue(getButtonDangerColorBackground(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
export const getButtonDangerColorBorder = (theme)=>theme.buttonDangerColorBorder || themeValueDefaults.buttonDangerColorBorder;
export const getButtonDangerColorBorderHovered = (theme)=>{
    const borderColor = getButtonDangerColorBorder(theme);
    return borderColor && getBrand700Color(borderColor);
};
export const getButtonDangerColorBorderPressed = (theme)=>getButtonDangerColorBorderHovered(theme);
export const getButtonDangerColorText = (theme)=>theme.buttonDangerColorText || themeValueDefaults.buttonDangerColorText;
export const getButtonDangerButtonIcon = (theme)=>getAccessibleColor(getButtonDangerColorText(theme), [
        150,
        0
    ]);
// Button Padding
export const getButtonPaddingX = (theme, isMobileSdkTheme)=>theme.buttonPaddingX || (isMobileSdkTheme ? scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', MOBILE_SDK_THEME_DEFAULTS.buttonPaddingXScale) : scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', 1));
export const getButtonPaddingY = (theme, isMobileSdkTheme)=>theme.buttonPaddingY || (isMobileSdkTheme ? scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', MOBILE_SDK_THEME_DEFAULTS.buttonPaddingYScale) : scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', 0.5));
// Button Label
export const getButtonLabelFontSize = (theme, isMobileSdkTheme)=>theme.buttonLabelFontSize || (isMobileSdkTheme ? MOBILE_SDK_THEME_DEFAULTS.buttonLabelFontSize : theme.labelMdFontSize || themeValueDefaults.labelMdFontSize);
export const getButtonLabelFontWeight = (theme, isMobileSdkTheme)=>theme.buttonLabelFontWeight || (isMobileSdkTheme ? calculateEmphasizedFontWeight(MOBILE_SDK_THEME_DEFAULTS.buttonLabelFontWeight) : calculateEmphasizedFontWeight(theme.labelMdFontWeight || themeValueDefaults.labelMdFontWeight));
export const getButtonLabelTextTransform = (theme)=>theme.buttonLabelTextTransform || theme.labelMdTextTransform || themeValueDefaults.labelMdTextTransform;
// Primary Link
export const getActionPrimaryColorText = (theme)=>theme.actionPrimaryColorText || getPrimaryColor(theme);
export const getActionPrimaryColorTextHover = (theme)=>{
    return deriveColorFromValue(getActionPrimaryColorText(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
};
export const getColorPrimaryLinkIcon = (theme)=>theme.actionPrimaryColorText || getPrimaryColor(theme);
export const getColorPrimaryLinkIconHovered = (theme)=>{
    return deriveColorFromValue(getColorPrimaryLinkIcon(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
};
export const getActionPrimaryTextDecorationColor = (theme)=>theme.actionPrimaryTextDecorationColor || getActionPrimaryColorText(theme);
export const getActionPrimaryTextDecorationColorHovered = (theme)=>deriveColorFromValue(theme.actionPrimaryTextDecorationColor, [
        900,
        850
    ], [
        0,
        150
    ]);
export const getActionPrimaryTextTransform = (theme)=>theme.actionPrimaryTextTransform || themeValueDefaults.actionPrimaryTextTransform;
// Secondary Link
export const getActionSecondaryColorText = (theme)=>theme.actionSecondaryColorText || // SecondaryLinkText in sail-next is neutral700, #414552: https://sail.stripe.me/beta/tokens/all/
    getAccessibleColorFromBackground(theme, [
        900,
        200
    ], [
        0,
        700
    ]);
export const getColorSecondaryLinkIcon = (theme)=>getActionSecondaryColorText(theme);
export const getActionSecondaryColorTextHovered = (theme)=>{
    return deriveColorFromValue(theme.actionSecondaryColorText, [
        900,
        850
    ], [
        0,
        150
    ]);
};
export const getActionSecondaryTextDecorationColor = (theme)=>{
    return theme.actionSecondaryTextDecorationColor || getActionSecondaryColorText(theme);
};
export const getActionSecondaryTextDecorationColorHovered = (theme)=>deriveColorFromValue(theme.actionSecondaryTextDecorationColor, [
        900,
        850
    ], [
        0,
        150
    ]);
export const getActionSecondaryTextTransform = (theme)=>theme.actionSecondaryTextTransform || themeValueDefaults.actionSecondaryTextTransform;
// Border Colors
export const getColorBorder = (theme)=>theme.colorBorder || // ColorBorder in sail-next is neutral150, #d5dbe1: https://sail.stripe.me/beta/tokens/all/
    getAccessibleColorFromBackground(theme, [
        900,
        750
    ], [
        0,
        150
    ]);
export const getFormHighlightColorBorder = (theme)=>theme.formHighlightColorBorder || getAccessibleBorderColor(getPrimaryColor(theme), theme, true) || getAccessibleColorFromBackground(theme, [
        900,
        600
    ], [
        0,
        300
    ]);
export const getOffsetBackgroundColor = (theme)=>theme.offsetBackgroundColor || // OffsetBackground in sail-next is neutral50, #f6f8fa: https://sail.stripe.me/beta/tokens/all/
    getAccessibleColorFromBackground(theme, [
        900,
        850
    ], [
        0,
        50
    ]);
export const getFormBackgroundColor = (theme)=>theme.formBackgroundColor || getColorBackground(theme) || themeValueDefaults.formBackgroundColor;
// Text Colors
export const getColorText = (theme)=>theme.colorText || getAccessibleTextColor(getColorBackgroundForCalculations(theme));
export const getColorFormText = (theme)=>theme.colorText || getAccessibleTextColor(getFormBackgroundColor(theme));
// Secondary Text Colors
export const getColorSecondaryText = (theme)=>theme.colorSecondaryText || // SecondaryText in sail-next is neutral500, #687385: https://sail.stripe.me/beta/tokens/all/
    getAccessibleColorFromBackground(theme, [
        900,
        400
    ], [
        0,
        500
    ]);
export const getColorFormDisabledBackground = (theme)=>// Form (Disabled) background in sail-next is neutral50, #f6f8fa: https://sail.stripe.me/beta/tokens/color/#forms
    getAccessibleColorFromBackground(theme, [
        900,
        850
    ], [
        0,
        50
    ]);
export const getColorOnFormDisabledBackgroundAccent = (theme)=>// onForm (Disabled) background accent in sail-next is neutral25
    getAccessibleColorFromBackground(theme, [
        900,
        875
    ], [
        0,
        25
    ]);
export const getColorFormDisabledBackgroundAccent = (theme)=>// Form (Disabled) background accent in sail-next is neutral100, #e9edf2: https://sail.stripe.me/beta/tokens/color/#forms
    getAccessibleColorFromBackground(theme, [
        900,
        800
    ], [
        0,
        100
    ]);
export const getColorFormDisabledText = (theme)=>// Form (Disabled) text in sail-next is neutral300, #a3acba: https://sail.stripe.me/beta/tokens/color/#forms
    getAccessibleColorFromBackground(theme, [
        900,
        600
    ], [
        0,
        300
    ]);
// Form Colors
export const getFormAccentColor = (theme)=>theme.formAccentColor || getPrimaryColor(theme);
export const getColorFormTextHover = (theme)=>{
    const textColor = getColorFormText(theme);
    return textColor && getFormTextHoverColor(textColor);
};
// The color of the placeholder text in form fields. Derives the color for fall back
export const getFormPlaceholderTextColor = (theme)=>{
    const backgroundColor = getFormBackgroundColor(theme);
    return theme.formPlaceholderTextColor || getAccessibleColorFromBackgroundValue(backgroundColor, [
        900,
        750
    ], [
        0,
        475
    ]);
};
// Form input field padding
export const getInputFieldPaddingX = (theme, isMobileSdkTheme)=>theme.inputFieldPaddingX || (isMobileSdkTheme ? scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', MOBILE_SDK_THEME_DEFAULTS.inputFieldPaddingXScale) : scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', 1));
export const getInputFieldPaddingY = (theme, isMobileSdkTheme)=>theme.inputFieldPaddingY || (isMobileSdkTheme ? scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', MOBILE_SDK_THEME_DEFAULTS.inputFieldPaddingYScale) : scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', 0.5));
// Table
export const getTableRowPaddingY = (theme)=>theme.tableRowPaddingY || theme.spacingUnit || themeValueDefaults.tableRowPaddingY;
// Badges
export const getBadgeDangerColorText = (theme)=>theme.badgeDangerColorText || themeValueDefaults.badgeDangerColorText;
export const getBadgeDangerColorBackground = (theme)=>theme.badgeDangerColorBackground || getAccessibleColor(getBadgeDangerColorText(theme), 500);
export const getBadgeDangerColorBorder = (theme)=>theme.badgeDangerColorBorder || getAccessibleColor(getBadgeDangerColorText(theme), 300);
export const getBadgeNeutralColorText = (theme)=>theme.badgeNeutralColorText || themeValueDefaults.badgeNeutralColorText;
export const getBadgeNeutralColorBorder = (theme)=>theme.badgeNeutralColorBorder || getAccessibleColor(getBadgeNeutralColorText(theme), 300);
export const getBadgeNeutralColorBackground = (theme)=>theme.badgeNeutralColorBackground || getAccessibleColor(getBadgeNeutralColorText(theme), 500);
export const getBadgeSuccessColorText = (theme)=>theme.badgeSuccessColorText || themeValueDefaults.badgeSuccessColorText;
export const getBadgeSuccessColorBorder = (theme)=>theme.badgeSuccessColorBorder || getAccessibleColor(getBadgeSuccessColorText(theme), 300);
export const getBadgeSuccessColorBackground = (theme)=>theme.badgeSuccessColorBackground || getAccessibleColor(getBadgeSuccessColorText(theme), 500);
export const getBadgeWarningColorText = (theme)=>theme.badgeWarningColorText || themeValueDefaults.badgeWarningColorText;
export const getBadgeWarningColorBorder = (theme)=>{
    if (theme.badgeWarningColorBorder) {
        return theme.badgeWarningColorBorder;
    }
    const accessibleColor = getAccessibleColor(getBadgeWarningColorText(theme), 300);
    const colorRgb = getColor(accessibleColor).rgb();
    colorRgb.b = Math.max(colorRgb.b - 46, 0); // Warning is special - we intentionally make the color less blue and more green, to go from orange to yellow
    colorRgb.g = Math.min(colorRgb.g + 13, 255);
    return colorRgb.formatHex();
};
export const getBadgeLabelFontSize = (theme)=>theme.badgeLabelFontSize || theme.labelSmFontSize || themeValueDefaults.labelSmFontSize;
export const getBadgeLabelFontWeight = (theme)=>theme.badgeLabelFontWeight || theme.labelSmFontWeight || themeValueDefaults.labelSmFontWeight;
export const getBadgeLabelTextTransform = (theme)=>theme.badgeLabelTextTransform || theme.labelSmTextTransform || themeValueDefaults.labelSmTextTransform;
export const getBadgePaddingX = (theme)=>theme.badgePaddingX || scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', 0.75);
export const getBadgePaddingY = (theme)=>theme.badgePaddingY || scaleTokenNumber(theme.spacingUnit || themeValueDefaults.spacingUnit, 'px', 0.25);
export const getBadgeWarningColorBackground = (theme)=>{
    if (theme.badgeWarningColorBackground) {
        return theme.badgeWarningColorBackground;
    }
    const accessibleColor = getAccessibleColor(getBadgeWarningColorText(theme), 500);
    const colorRgb = getColor(accessibleColor).rgb();
    colorRgb.b = Math.max(colorRgb.b - 28, 0); // Warning is special - we intentionally make the color less blue and more green, to go from orange to yellow
    colorRgb.g = Math.min(colorRgb.g + 8, 255);
    return colorRgb.formatHex();
};
// Border Radius
export const getButtonBorderRadius = (theme)=>theme.buttonBorderRadius || theme.borderRadius || themeValueDefaults.buttonBorderRadius;
export const getFormBorderRadius = (theme)=>theme.formBorderRadius || theme.borderRadius || themeValueDefaults.formBorderRadius;
export const getBadgeBorderRadius = (theme)=>theme.badgeBorderRadius || theme.borderRadius || themeValueDefaults.badgeBorderRadius;
export const getOverlayBorderRadius = (theme)=>theme.overlayBorderRadius || theme.borderRadius || themeValueDefaults.overlayBorderRadius;
export const getOverlayBackdropColor = (theme)=>theme.overlayBackdropColor || `${getAccessibleColorFromBackground(theme, [
        900,
        700
    ], [
        0,
        200
    ])}B3`; // We calculate the backdrop color from the background, and append this number to achieve 70% opacity
// Shadows
export const getShadowMedium = (theme)=>{
    // Shadow medium in sail-next light mode is "0px 3px 6px 0px rgba(0, 0, 0, 0.12), 0px 7px 14px 0px rgba(48, 49, 61, 0.08)"
    // Shadow medium in sail-next dark mode is not defined
    // https://sail.stripe.me/ui/tokens/shadow/
    // We simply decide which ones of the 2 to use depending on the contrast of the background color, similar to when we decide whether to use white or black text color
    const lightModeShadow = '0px 3px 6px 0px rgba(0, 0, 0, 0.12), 0px 7px 14px 0px rgba(48, 49, 61, 0.08)';
    const darkModeShadow = '0px 2px 4px rgba(0, 0, 0, 0.4), 0px 8px 16px rgba(0, 0, 0, 0.5)';
    const contrastToUse = shouldUseDarkOrLightContrast(getColorBackgroundForCalculations(theme));
    switch(contrastToUse){
        case 'dark':
            return darkModeShadow;
        case 'light':
            return lightModeShadow;
        default:
            assertUnreachable(contrastToUse);
            return lightModeShadow;
    }
};
// Font Size
const getFontSizeBase = (theme)=>theme.fontSizeBase || themeValueDefaults.fontSizeBase;
// Headings
export const getHeadingXlFontSize = (theme)=>theme.headingXlFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingXlFontSize, 'px')) || themeValueDefaults.headingXlFontSize;
export const getHeadingLgFontSize = (theme)=>theme.headingLgFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingLgFontSize, 'px')) || themeValueDefaults.headingLgFontSize;
export const getHeadingMdFontSize = (theme)=>theme.headingMdFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingMdFontSize, 'px')) || themeValueDefaults.headingMdFontSize;
export const getHeadingSmFontSize = (theme)=>theme.headingSmFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingSmFontSize, 'px')) || themeValueDefaults.headingSmFontSize;
export const getHeadingXsFontSize = (theme)=>theme.headingXsFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingXsFontSize, 'px')) || themeValueDefaults.headingXsFontSize;
// Body
export const getBodyMdFontSize = (theme)=>theme.bodyMdFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.bodyMdFontSize, 'px')) || themeValueDefaults.bodyMdFontSize;
export const getBodySmFontSize = (theme)=>theme.bodySmFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.bodySmFontSize, 'px')) || themeValueDefaults.bodySmFontSize;
// Label
export const getLabelMdFontSize = (theme)=>theme.labelMdFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.labelMdFontSize, 'px')) || themeValueDefaults.labelMdFontSize;
export const getLabelSmFontSize = (theme)=>theme.labelSmFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.labelSmFontSize, 'px')) || themeValueDefaults.labelSmFontSize;
export const getFocusRingColor = (theme)=>{
    const primaryColor = getPrimaryColor(theme);
    const visibleFocusRingColor = getAccessibleBorderColor(primaryColor, theme, true);
    return `0 0 0 4px ${getColor(visibleFocusRingColor).copy({
        opacity: 0.25
    }).formatRgb()}`;
};
export const getFocusRingColorHex = (theme)=>{
    const primaryColor = getPrimaryColor(theme);
    const visibleFocusRingColor = getAccessibleBorderColor(primaryColor, theme, true);
    return `${getColor(visibleFocusRingColor).formatHex()}40`;
};
export const mobileSdkFocusRing = '0 0 0 0 transparent';
export const getMobileSdkInputFocusRingOverride = (theme, useMobileSdkTheme)=>{
    if (!useMobileSdkTheme) {
        return [];
    }
    return [
        assignTokens(tokens.focus, getFocusRingColor(theme))
    ];
};
export const getBrandColors0To900Intents = (theme)=>{
    const { color } = tokens;
    const primaryColor = getPrimaryColor(theme);
    // Note these values will be different in light/dark mode
    const backgroundColorForCalculations = getColorBackgroundForCalculations(theme);
    const invertedBackgroundColor = invertColor(backgroundColorForCalculations);
    // Reference values are from sail-next: https://livegrep.corp.stripe.com/view/stripe-internal/mint/pay-server/frontend/sail-next/packages/ui/src/themes/darkMode.ts?commit=c6257032d38cf5fc#L90
    return [
        // The primary color is the brand600 color. From there, we derive the rest
        [
            primaryColor,
            color.primary[600]
        ],
        // To calculate the <600 colors, we derive them by interpolating between the primary color and the background color. The closer to 0, the more similar to the background color
        [
            blendColors(backgroundColorForCalculations, primaryColor, [
                0,
                600
            ], 500),
            color.primary[500]
        ],
        [
            blendColors(backgroundColorForCalculations, primaryColor, [
                0,
                600
            ], 400),
            color.primary[400]
        ],
        [
            blendColors(backgroundColorForCalculations, primaryColor, [
                0,
                600
            ], 300),
            color.primary[300]
        ],
        [
            blendColors(backgroundColorForCalculations, primaryColor, [
                0,
                600
            ], 200),
            color.primary[200]
        ],
        [
            blendColors(backgroundColorForCalculations, primaryColor, [
                0,
                600
            ], 100),
            color.primary[100]
        ],
        [
            blendColors(backgroundColorForCalculations, primaryColor, [
                0,
                600
            ], 50),
            color.primary[50]
        ],
        [
            blendColors(backgroundColorForCalculations, primaryColor, [
                0,
                600
            ], 25),
            color.primary[25]
        ],
        [
            blendColors(backgroundColorForCalculations, primaryColor, [
                0,
                600
            ], 0),
            color.primary[0]
        ],
        // To derive the >600 colors, we interpolate between the primary color and the inverted background color. The closer to 1000, the more similar to the inverted background color
        [
            blendColors(primaryColor, invertedBackgroundColor, [
                600,
                1000
            ], 700),
            color.primary[700]
        ],
        [
            blendColors(primaryColor, invertedBackgroundColor, [
                600,
                1000
            ], 800),
            color.primary[800]
        ],
        [
            blendColors(primaryColor, invertedBackgroundColor, [
                600,
                1000
            ], 900),
            color.primary[900]
        ]
    ];
};
export const getNeutralColors0To900Intents = (theme)=>{
    const { color } = tokens;
    const neutral600Lightmode = '#50617A'; // This is the neutral600 value in sail next in light mode
    const neutral600Darkmode = '#8c99ad'; // This is the neutral600 value in sail next in dark mode
    // Note the rest of the neutral values will be different in light/dark mode
    const backgroundColorForCalculations = getColorBackgroundForCalculations(theme);
    const invertedBackgroundColor = invertColor(backgroundColorForCalculations);
    const contrastToUse = shouldUseDarkOrLightContrast(backgroundColorForCalculations);
    let neutral600;
    switch(contrastToUse){
        case 'dark':
            neutral600 = neutral600Darkmode;
            break;
        case 'light':
            neutral600 = neutral600Lightmode;
            break;
        default:
            assertUnreachable(contrastToUse);
            neutral600 = neutral600Lightmode;
    }
    // Reference values are from sail-next: https://livegrep.corp.stripe.com/view/stripe-internal/mint/pay-server/frontend/sail-next/packages/ui/src/themes/darkMode.ts?commit=c6257032d38cf5fc#L20 (the gray colors)
    return [
        // The neutral 600 is hardcoded. From there, we derive the rest
        [
            neutral600,
            color.neutral[600]
        ],
        // To calculate the <600 colors, we derive them by interpolating between the neutral 600 color and the background color. The closer to 0, the more similar to the background color
        [
            blendColors(backgroundColorForCalculations, neutral600, [
                0,
                600
            ], 500),
            color.neutral[500]
        ],
        [
            blendColors(backgroundColorForCalculations, neutral600, [
                0,
                600
            ], 400),
            color.neutral[400]
        ],
        [
            blendColors(backgroundColorForCalculations, neutral600, [
                0,
                600
            ], 300),
            color.neutral[300]
        ],
        [
            blendColors(backgroundColorForCalculations, neutral600, [
                0,
                600
            ], 200),
            color.neutral[200]
        ],
        [
            blendColors(backgroundColorForCalculations, neutral600, [
                0,
                600
            ], 100),
            color.neutral[100]
        ],
        [
            blendColors(backgroundColorForCalculations, neutral600, [
                0,
                600
            ], 50),
            color.neutral[50]
        ],
        [
            blendColors(backgroundColorForCalculations, neutral600, [
                0,
                600
            ], 25),
            color.neutral[25]
        ],
        [
            blendColors(backgroundColorForCalculations, neutral600, [
                0,
                600
            ], 0),
            color.neutral[0]
        ],
        // To derive the >600 colors, we interpolate between the neutral 600 color and the inverted background color. The closer to 1000, the more similar to the inverted background color
        [
            blendColors(neutral600, invertedBackgroundColor, [
                600,
                1000
            ], 700),
            color.neutral[700]
        ],
        [
            blendColors(neutral600, invertedBackgroundColor, [
                600,
                1000
            ], 800),
            color.neutral[800]
        ],
        [
            blendColors(neutral600, invertedBackgroundColor, [
                600,
                1000
            ], 900),
            color.neutral[900]
        ]
    ];
};

//# sourceMappingURL=tokenOverrides.js.map